import type { ApiResult } from '../../api.js';
import { useRequest, type RequestState } from '../../ui/use-request.js';

export type LazyRequestState<T> = Omit<RequestState<T>, 'status'> & { status: RequestState<T>['status'] | 'idle' };

/**
 * `useRequest` that does not start until `enabled` is true — for a record page's lazy regions
 * (06-modules §5.3: R3 and R4 may load when their tab is opened or deep-linked). While disabled it
 * reports `idle`, so a region never shows a skeleton for a request that was never sent.
 */
export function useLazyRequest<T>(load: (signal: AbortSignal) => Promise<ApiResult<T>>, deps: readonly unknown[], enabled: boolean): LazyRequestState<T> {
  // Why: a promise that never settles keeps the shared hook in its initial state; the abort on a
  // dependency change discards it, so nothing leaks beyond the closure.
  const state = useRequest<T>((signal) => (enabled ? load(signal) : new Promise<ApiResult<T>>(() => {})), [...deps, enabled]);
  if (!enabled && state.data === null) return { ...state, status: 'idle' };
  return state;
}
